<template>
  <div class="terms-of-service min-h-screen bg-gray-50">
    <!-- Header -->
    <header class="bg-white border-b border-gray-200 py-4 mb-8">
      <div class="container mx-auto px-4">
        <div class="flex items-center justify-between">
          <div class="flex items-center gap-2">
            <img src="@/assets/logo.png" alt="Salasah" class="h-8 w-auto" />
            <span class="text-lg font-semibold text-gray-800">Salasah</span>
          </div>
        </div>
      </div>
    </header>

    <!-- Main Content -->
    <main class="container mx-auto px-4 py-8 max-w-4xl">
      <div class="bg-white rounded-lg shadow-sm p-8">
        <!-- Title -->
        <h1 class="text-3xl font-bold text-gray-900 mb-8 text-center">Terms of Service</h1>

        <!-- Introduction -->
        <div class="mb-12 text-lg text-gray-700 leading-relaxed">
          <p class="mb-4">
            Please read this End User License Agreement and Terms of Service (this "EULA") carefully as this EULA constitutes a binding contract between you, an individual user ("you") and Salasah FZ-LLC ("Salasah") governing your use of the Salasah website found at https://Salasah.sa ("Website"), any mobile applications (each an "App"), or other Internet services under Salasah's control and used to provide Salasah's services to you (the Website, Apps, and those Internet services are, collectively, the "Services").
          </p>
          <p class="mb-4">
            By registering on the sign-up page, subscribing to the Company's Services, or otherwise accessing or using the Services, you have read, understood, and agree to be bound by and comply with the terms and conditions of this EULA. If you are using the Services on behalf of an entity, partnership, or other organization, then you represent and warrant that you: (i) are an authorized representative of that entity with the authority to bind that entity to this EULA, and (ii) agree to be bound by this EULA on behalf of that entity. If you do not agree, Customer (as defined below) is not permitted to use the Services.
          </p>
          <p class="mb-4">
            Any dispute, difference, controversy or claim arising out of or in connection with this contract, including (but not limited to) any question regarding its existence, validity, interpretation, performance, discharge and applicable remedies, shall be subject to the exclusive jurisdiction of the Courts of the Saudi Arabia International Financial Centre ("the KSA Courts").
          </p>
          <p class="mb-4">
            Salasah recommends that you print a copy of this Agreement for your records.
          </p>
        </div>

        <!-- Main Sections -->
        <div class="space-y-12">
          <!-- Definitions Section -->
          <section class="border-b border-gray-100 pb-8">
            <h2 class="text-2xl font-bold text-[#0f4640] mb-6">1. Definitions</h2>
            <div class="space-y-4 text-gray-700">
              <ul class="space-y-4">
                <li>
                  <span class="font-semibold">"Customer"</span> means you or, if you are using the Service on behalf of a company, entity, or organization, the entity, partnership or organization on whose behalf you are utilizing the Services.
                </li>
                <li>
                  <span class="font-semibold">"Party"</span> means either Customer or Salasah and "Parties" means both Customer and Salasah.
                </li>
                <li>
                  <span class="font-semibold">"Recipient"</span> means a Customer who is an accountant, accounting firm, or other third party whose clients include Salasah customers. Recipients access the Services in accordance with this Agreement for the purposes of obtaining, with their clients' authorization, Customer Data about such clients to provide them services.
                </li>
                <li>
                  <span class="font-semibold">"Representative"</span> means a representative of Customer, including an accountant, accounting firm, or other third party, to whom Customer provides (or for whom Customer authorizes Salasah to provide) access to Customer's Data stored via the Services for the purpose of providing Customer professional services.
                </li>
                <li>
                  <span class="font-semibold">"Sign-up"</span> means the on-line sign-up process on Salasah's website.
                </li>
                <li>
                  <span class="font-semibold">"Term"</span> has the meaning set out in Section 6.
                </li>
              </ul>
            </div>
          </section>

          <!-- Subscription Section -->
          <section class="border-b border-gray-100 pb-8">
            <h2 class="text-2xl font-bold text-[#0f4640] mb-6">2. Subscribing to the Service; Eligibility</h2>
            <div class="space-y-4 text-gray-700">
              <p>Customer subscribes for the Services by selecting them from the options available during Sign-up. In the event of any conflict between this EULA and the information provided during Sign-up, this EULA shall control. Customer and all Customer Personnel (as defined below) must be at least 18 years of age to use the Services. By agreeing to this EULA, you represent and warrant to Salasah that: (a) you are at least 18 years of age; (b) you have not previously been suspended or removed from the Services; and © your registration and your use of the Services is in compliance with all applicable laws and regulations.</p>
              <p>Customer agrees not to have or register for more than one account without express written permission from Salasah. Salasah reserves the right to suspend an account if it is determined that the user has violated these terms of service.</p>
            </div>
          </section>

          <!-- License Section -->
          <section class="border-b border-gray-100 pb-8">
            <h2 class="text-2xl font-bold text-[#0f4640] mb-6">3. License</h2>
            <div class="space-y-4 text-gray-700">
              <h3 class="text-xl font-semibold mb-3">License Grant</h3>
              <p>Subject to Customer's complete and ongoing compliance with all the terms and conditions of this EULA and payment of all applicable fees, Salasah hereby grants to Customer a personal, limited, revocable, non-exclusive, non-sublicensable, non-transferable license during the Term, to access and use the Services through Salasah's available interfaces and to permit Customer Personnel (as defined below) to access and use the Services through Salasah's available interfaces, in each case, solely in connection with Customer's internal business operations.</p>
              
              <h3 class="text-xl font-semibold mb-3">User IDs</h3>
              <p>Upon Customer's request, Salasah will issue a Customer ID to Customer. Customer may share this Customer ID with each of its partners, shareholders, employees and contractors who are bound by confidentiality restrictions at least as restrictive as this EULA ("Customer Personnel"). Customer Personnel may only access and use the Service through the Customer ID and in compliance with this EULA. Customer will not allow Customer Personnel to share the Customer ID with third parties. Customer is responsible for all activity occurring under its Customer ID.</p>

              <h3 class="text-xl font-semibold mb-3">Internet Connectivity; Disclaimer</h3>
              <p>Salasah may make the Services available for access via the Internet. Customer shall provide, at Customer's own expense, all necessary hardware, applications and Internet connectivity necessary to access the Services. Customer acknowledges that the Internet is known to be unpredictable in performance, unsecure and may, from time to time, impede access to the Services or performance hereunder.</p>
            </div>
          </section>

          <!-- Proprietary Rights Section -->
          <section class="border-b border-gray-100 pb-8">
            <h2 class="text-2xl font-bold text-[#0f4640] mb-6">4. Proprietary Rights</h2>
            <div class="space-y-4 text-gray-700">
              <p>Salasah expressly reserves all rights in the Services and all other materials provided by Salasah hereunder not specifically granted to Customer. It is acknowledged that all right, title and interest in the Services and all other materials provided by Salasah hereunder, any update, adaptation, translation, customization or derivative work thereof, and all intellectual property rights therein will remain with Salasah (or third party suppliers, if applicable) and that the Services and all other materials provided by Salasah hereunder are licensed on a subscription basis and not "sold" to Customer.</p>
            </div>
          </section>

          <!-- License Limitations Section -->
          <section class="border-b border-gray-100 pb-8">
            <h2 class="text-2xl font-bold text-[#0f4640] mb-6">Limitations</h2>
            <div class="space-y-4 text-gray-700">
              <p>Customer agrees that it will not, and it will not permit any Customer Personnel or any other party to:</p>
              <ul class="list-disc space-y-2 ml-6">
                <li>Permit any party to access or use the Services other than the Customer Personnel authorized under this EULA;</li>
                <li>Modify, adapt, alter or translate any software underlying the Services, except as expressly allowed hereunder;</li>
                <li>Sublicense, lease, rent, loan, distribute, or otherwise transfer the Service to any third party;</li>
                <li>Reverse engineer, decompile, disassemble, or otherwise derive or determine or attempt to derive or determine the source code;</li>
                <li>Use or copy any software underlying the Services except as expressly allowed hereunder;</li>
                <li>Conduct or promote any illegal activities while using the Services;</li>
                <li>Use the Services to generate unsolicited email advertisements or spam;</li>
                <li>Use the Services to stalk, harass or harm another individual;</li>
                <li>Interfere in any way with the proper functioning of the Services;</li>
                <li>Attempt to gain access to secured portions of the Services to which it does not possess access rights;</li>
                <li>Upload or transmit any form of virus, worm, Trojan horse, or other malicious code;</li>
                <li>Impersonate any person or entity, or otherwise misrepresent its affiliation with a person or entity;</li>
              </ul>
            </div>
          </section>

          <!-- Term Section -->
          <section class="border-b border-gray-100 pb-8">
            <h2 class="text-2xl font-bold text-[#0f4640] mb-6">6. Term</h2>
            <div class="space-y-4 text-gray-700">
              <p>The term of this EULA ("Term") shall commence upon the date the sign-up process is complete and will continue on a month to month basis for so long as Customer is current on all fees due unless or until terminated as permitted under this EULA. If Customer has elected to receive 1 free month of financial statements, notwithstanding anything else to the contrary herein, the Term will begin on the date the sign-up process is complete and end upon delivery of the 1 free month of financial statements as communicated by Salasah (Pilot Term"). At the end of the Pilot Term, if Customer wishes to continue to receive the Services, Customer must begin paying for the Services.</p>
            </div>
          </section>

          <!-- Customer Support Section -->
          <section class="border-b border-gray-100 pb-8">
            <h2 class="text-2xl font-bold text-[#0f4640] mb-6">7. Customer Support</h2>
            <div class="space-y-4 text-gray-700">
              <h3 class="text-xl font-semibold mb-3">Technical Support</h3>
              <p>During the Term for the applicable Services, and for so long as Customer is current with its payment of all applicable fees, Salasah will use commercially reasonable efforts to provide Customer with technical support services relating to the Services via its technical support website, email, or telephone.</p>

              <h3 class="text-xl font-semibold mb-3">Web Support</h3>
              <p>Web support shall not include any tax or other professional or expert advice of any kind, including, but not limited to any advice regarding the appropriate handling of tax and accounting issues, or otherwise.</p>

              <h3 class="text-xl font-semibold mb-3">Service Upgrades and Scheduled Downtime</h3>
              <p>Salasah may update the Services in its sole discretion. Salasah may from time to time schedule downtime for maintenance and upgrades.</p>

              <h3 class="text-xl font-semibold mb-3">Data Storage & Backup</h3>
              <p>Salasah is not responsible for performing, and is not liable for any failure to perform, any back-up of any data (including Customer Data) provided, transmitted, processed, or stored by Customer in or through the Services. It is Customer's responsibility to backup onto Customer's own local system all Customer Data.</p>
            </div>
          </section>

          <!-- Fees & Payment Section -->
          <section class="border-b border-gray-100 pb-8">
            <h2 class="text-2xl font-bold text-[#0f4640] mb-6">8. Fees & Payment</h2>
            <div class="space-y-4 text-gray-700">
              <h3 class="text-xl font-semibold mb-3">Fees</h3>
              <p>Access to the Services may require Customer to pay fees. Before Customer pays any fees, Customer will have an opportunity to review and accept the fees that Customer will be charged. All fees are in SR Riyals and are, except as described in this Section 8, non-refundable.</p>

              <h3 class="text-xl font-semibold mb-3">Automatic Credit Card Payments</h3>
              <p>The Services may include functionality for activating, updating, or canceling recurring payments for periodic charges. If Customer activates or updates recurring payments through the Service, Customer authorizes Salasah to periodically charge, on a going-forward basis and until cancellation of either the recurring payments or Customer's account.</p>

              <h3 class="text-xl font-semibold mb-3">Late Payment</h3>
              <p>Customer may not withhold or "setoff" any amounts due hereunder. Salasah reserves the right to suspend the Services until all past due amounts are paid in full.</p>

              <h3 class="text-xl font-semibold mb-3">Refunds</h3>
              <p>In the event of termination of the Services for any reason except for Customer's breach, Salasah may provide Customer with a refund of any pre-paid, but unused fees related to such Services, subject to certain conditions.</p>
            </div>
          </section>

          <!-- Contact Section -->
          <section class="bg-[#faf6df] rounded-lg p-8">
            <h2 class="text-2xl font-bold text-[#0f4640] mb-6">Contact Us</h2>
            <div class="text-gray-700">
              <p class="mb-6 text-lg">
                Notices and inquiries to Salasah can be sent via:
              </p>
              <div class="bg-white rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow duration-300">
                <a href="mailto:help@Salasah.sa" class="flex items-center gap-3 text-[#0f4640] hover:text-[#0f4640]/80 transition-colors">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  <span class="text-lg font-medium">help@Salasah.sa</span>
                </a>
              </div>
            </div>
          </section>

          <!-- Confidentiality Section -->
          <section class="border-b border-gray-100 pb-8">
            <h2 class="text-2xl font-bold text-[#0f4640] mb-6">9. Confidential & Proprietary Information</h2>
            <div class="space-y-4 text-gray-700">
              <h3 class="text-xl font-semibold mb-3">Definitions</h3>
              <p>For purposes of this section, a Party receiving Confidential & Proprietary Information shall be the "Recipient" and the Party disclosing such information shall be the "Discloser". Customer acknowledges that the Service will be considered Confidential and Proprietary Information belonging exclusively to Salasah, and Salasah acknowledges that Customer Data will be considered Confidential and Proprietary Information belonging exclusively to Customer.</p>

              <h3 class="text-xl font-semibold mb-3">Covenant</h3>
              <p>Recipient hereby agrees that during the Term and at all times thereafter it shall not:</p>
              <ul class="list-disc space-y-2 ml-6">
                <li>Disclose such Confidential & Proprietary Information of the Discloser to any person or entity, except to its own personnel having a "need to know"</li>
                <li>Use Confidential & Proprietary Information of the Discloser except to exercise its license rights or perform its obligations under this EULA</li>
                <li>Alter or remove from any Confidential & Proprietary Information of the Discloser any proprietary legend</li>
              </ul>
            </div>
          </section>

          <!-- Warranties Section -->
          <section class="border-b border-gray-100 pb-8">
            <h2 class="text-2xl font-bold text-[#0f4640] mb-6">10. No Warranties</h2>
            <div class="space-y-4 text-gray-700">
              <p>THE SERVICES AND ALL MATERIAL OR CONTENT AVAILABLE THROUGH THE SERVICE ARE PROVIDED "AS IS", "AS AVAILABLE", WITH ALL FAULTS AND WITHOUT WARRANTIES, REPRESENTATIONS OR CONDITIONS OF ANY KIND. TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, SALASAH HEREBY DISCLAIMS ALL WARRANTIES, EXPRESS, IMPLIED, STATUTORY, OR OTHERWISE.</p>
              <p>SOME JURISDICTIONS MAY PROHIBIT A DISCLAIMER OF WARRANTIES AND CUSTOMER MAY HAVE OTHER RIGHTS THAT VARY FROM JURISDICTION TO JURISDICTION.</p>
            </div>
          </section>

          <!-- Liability Section -->
          <section class="border-b border-gray-100 pb-8">
            <h2 class="text-2xl font-bold text-[#0f4640] mb-6">11. Limitation of Liabilities</h2>
            <div class="space-y-4 text-gray-700">
              <p>IN NO EVENT WILL SALASAH BE LIABLE FOR DAMAGES ARISING OUT OF OR IN ANY WAY CONNECTED TO THIS EULA EXCEEDING FEES PAID IN RESPECT OF THE SERVICES AT ISSUE IN THE LAST THREE (3) MONTHS.</p>
              <p>IN NO EVENT SHALL SALASAH BE LIABLE TO CUSTOMER FOR ANY:</p>
              <ul class="list-disc space-y-2 ml-6">
                <li>Special, indirect, incidental, punitive or consequential damages</li>
                <li>Lost savings, profits, data, use, or goodwill</li>
                <li>Business interruption even if notified in advance of such possibility</li>
                <li>Personal or property damage arising out of or in any way connected to this EULA</li>
              </ul>
            </div>
          </section>

          <!-- Termination Section -->
          <section class="border-b border-gray-100 pb-8">
            <h2 class="text-2xl font-bold text-[#0f4640] mb-6">13. Termination</h2>
            <div class="space-y-4 text-gray-700">
              <h3 class="text-xl font-semibold mb-3">On Notice</h3>
              <p>Either Party can terminate this EULA for their convenience by providing the other party with notice of termination prior to the last day of the then current monthly renewal.</p>

              <h3 class="text-xl font-semibold mb-3">Generally</h3>
              <p>Either Party may terminate this EULA if the other Party breaches any material provision thereof and fails within fifteen (15) days after receipt of notice of default to correct such material breach.</p>

              <h3 class="text-xl font-semibold mb-3">Survival</h3>
              <p>Upon termination or expiration of this EULA for any reason, all rights and obligations of both Parties (except for Customer's payment of all sums then owing), including all licenses granted hereunder, shall immediately terminate except as provided below.</p>
            </div>
          </section>

          <!-- General Provisions Section -->
          <section class="border-b border-gray-100 pb-8">
            <h2 class="text-2xl font-bold text-[#0f4640] mb-6">14. General Provisions</h2>
            <div class="space-y-4 text-gray-700">
              <h3 class="text-xl font-semibold mb-3">Assignment</h3>
              <p>Customer may not assign this EULA or any of its rights or obligations hereunder to any third party without Salasah's prior written consent. Any assignment in violation of this section shall be void.</p>

              <h3 class="text-xl font-semibold mb-3">Arbitration</h3>
              <p>Any dispute, difference, controversy or claim arising out of or in connection with this contract shall be subject to the exclusive jurisdiction of the Courts of the Saudi Arabia International Financial Centre ("the KSA Courts").</p>

              <h3 class="text-xl font-semibold mb-3">Force Majeure</h3>
              <p>Customer acknowledges and understands that if Salasah is unable to provide Services as a result of a force majeure event Salasah will not be in breach of this EULA and will not be liable for delays caused by such event.</p>
            </div>
          </section>
        </div>

        <!-- Last Update -->
        <div class="mt-12 pt-8 border-t border-gray-100 text-center text-gray-500 text-sm">
          Last updated: January 28, 2024
        </div>
      </div>
    </main>
  </div>
</template>

<script>
export default {
  name: 'TermsOfService'
}
</script>

<style scoped>
.terms-of-service {
  font-family: system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
}

.list-disc {
  list-style-type: disc;
}

.list-disc li {
  margin-left: 1.5rem;
  padding-left: 0.5rem;
}
</style> 